import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { encrypt, decrypt } from './crypto.util';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

@Injectable({ providedIn: 'root' })
export class LockService {
  private readonly IDLE_TIME = 10 * 60 * 1000;
  private readonly STORAGE_KEY = 'ls';

  private timeout!: ReturnType<typeof setTimeout>;
  private lockedSubject = new BehaviorSubject<boolean>(false);
  readonly locked$ = this.lockedSubject.asObservable();

  // 🔑 routes that must NEVER be locked
  private readonly EXCLUDED_PREFIXES = [
    '/authentication',
    '/public',
  ];

  constructor(private router: Router, private dialog: MatDialog) {
    this.restoreFromSession();
    this.listenToRouteChanges();
  }

  start() {
    if (this.shouldIgnoreLock()) return;
    this.resetTimer();
  }

  registerActivity() {
    if (this.isLocked() || this.shouldIgnoreLock()) return;
    this.resetTimer();
  }

  async lock() {
    if (this.shouldIgnoreLock()) return;
    this.dialog.closeAll()
    this.clearTimer();
    this.lockedSubject.next(true);
    await this.persist(true);
  }

  async unlock() {
    this.lockedSubject.next(false);
    await this.persist(false);

    if (!this.shouldIgnoreLock()) {
      this.resetTimer();
    }
  }

  isLocked(): boolean {
    return this.lockedSubject.value;
  }

  // ---------------- private helpers ----------------

  private resetTimer() {
    this.clearTimer();
    this.timeout = setTimeout(() => this.lock(), this.IDLE_TIME);
  }

  private clearTimer() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private async persist(value: boolean) {
    const encrypted = await encrypt(JSON.stringify({ locked: value }));
    sessionStorage.setItem(this.STORAGE_KEY, encrypted);
  }

  private async restoreFromSession() {
    const stored = sessionStorage.getItem(this.STORAGE_KEY);
    if (!stored) return;

    const decrypted = await decrypt(stored);
    if (!decrypted) return;

    const { locked } = JSON.parse(decrypted);
    this.lockedSubject.next(locked);

    if (!locked && !this.shouldIgnoreLock()) {
      this.resetTimer();
    }
  }

  // 🔑 route-based suppression
  private shouldIgnoreLock(): boolean {
    const url = this.router.url || '/';
    return this.EXCLUDED_PREFIXES.some(prefix =>
      url === prefix || url.startsWith(prefix + '/')
    );
  }

  // 🔄 auto-unlock when navigating to auth/public
  private listenToRouteChanges() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.shouldIgnoreLock()) {
          this.clearTimer();
          if (this.isLocked()) {
            this.lockedSubject.next(false);
            this.persist(false);
          }
        } else if (!this.isLocked()) {
          this.resetTimer();
        }
      });
  }
}
